/**
 * Tranquira Evenfall page script (templates/product.heated-slippers.json).
 * Loaded by sections/ef-gallery.liquid right after pdp.js. pdp.js keeps every
 * bra behaviour (gallery, accordions, reveals, below-the-fold sections); this
 * module owns only the Evenfall parts: the daily flash rotation (the bar in the
 * top slot, countdown, midnight rollover, --flash-bar-h), the buy box (bundle picker, price maths,
 * power-banks line, perk line, add to cart through the existing cart
 * drawer), the assurance card (delivery window, timeline draw-in), the
 * Evenfall back-to-top disc, the social-proof cards, the reviews carousel
 * above the FAQ (arrows, dots, keyboard) and the sticky bar mirror. Cart mutations stay in cart-drawer.js (window.TranquiraCart.add,
 * and the 10,000mAh upgrade row inside the drawer); nothing here talks to
 * the Cart API directly except the no-drawer fallback, exactly like pdp.js.
 *
 * Pricing core: the block between the ef-pricing-core markers is byte
 * identical to extensions/evenfall-pricing/src/pricing.js in the app project
 * (~/dev/tranquira-admin-app/tranquira-admin-claude); the function's parity
 * test fails if the two drift. That function is not deployed and cannot run
 * on this store (Advanced plan; custom-app Functions are Plus only): the
 * checkout side is native automatic discounts (round 9), priced to the same
 * table, and offerFor is only used by the "rotation" preview.
 *
 * Time: the day's offer is the record Shopify Flow wrote to the shop metafield
 * evenfall.today when it opened that day's native discounts (round 9), honoured
 * only for today's date in New York (Intl, timeZone America/New_York), never
 * from Liquid alone - cached HTML can cross midnight. The countdown runs to
 * the next New York midnight (DST-safe); at zero the record no longer names
 * today, so the perk and the bar go away without a reload (ef:offer event)
 * until the page is loaded again with the new day's record.
 */

/* ef-pricing-core:start */
export const EF_PRICING = (() => {
  const toCents = (v) => Math.round(Number(v) * 100);
  const int = (v, d) => (Number.isFinite(Number(v)) ? Math.round(Number(v)) : d);
  function normalizeConfig(raw, unit) {
    const c = raw && typeof raw === 'object' ? raw : {};
    const tt = c.tier_total && typeof c.tier_total === 'object' ? c.tier_total : {};
    const conv = unit === 'cents'
      ? (v, d) => (v === undefined || v === null || v === '' ? d : int(v, d))
      : (v, d) => (v === undefined || v === null || v === '' || !Number.isFinite(Number(v)) ? d : toCents(v));
    return {
      rotationOn: c.rotation_on === true,
      startDate: typeof c.start_date === 'string' ? c.start_date.slice(0, 10) : '',
      firstOffer: c.first_offer === 'B' ? 'B' : 'A',
      tierTotal: { 1: conv(tt['1'], 6990), 2: conv(tt['2'], 10990), 3: conv(tt['3'], 13990) },
      upgrade: conv(c.upgrade, 1000),
      dayBOff: conv(c.day_b_off, 1000),
      currency: typeof c.currency === 'string' && c.currency ? c.currency : 'USD',
      productId: typeof c.product_id === 'string' ? c.product_id : '',
      optionName: typeof c.option_name === 'string' && c.option_name ? c.option_name : 'Power banks',
      upgradeValue: typeof c.upgrade_value === 'string' && c.upgrade_value ? c.upgrade_value : '2 x 10,000mAh'
    };
  }
  /** Julian day number of a calendar date (integer maths, no Date object). */
  function jdn(y, m, d) {
    const a = Math.floor((14 - m) / 12);
    const y2 = y + 4800 - a;
    const m2 = m + 12 * a - 3;
    return d + Math.floor((153 * m2 + 2) / 5) + 365 * y2 + Math.floor(y2 / 4) - Math.floor(y2 / 100) + Math.floor(y2 / 400) - 32045;
  }
  function parseDate(s) {
    const p = String(s || '').slice(0, 10).split('-').map((x) => parseInt(x, 10));
    if (p.length !== 3 || p.some((x) => !Number.isFinite(x)) || p[1] < 1 || p[1] > 12 || p[2] < 1 || p[2] > 31) return null;
    return p;
  }
  /** Today's offer for a store-local date "YYYY-MM-DD": 'off' | 'A' | 'B'. */
  function offerFor(cfg, localDate) {
    if (!cfg || !cfg.rotationOn) return 'off';
    const s = parseDate(cfg.startDate);
    const t = parseDate(localDate);
    if (!s || !t) return 'off';
    const idx = jdn(t[0], t[1], t[2]) - jdn(s[0], s[1], s[2]);
    const other = cfg.firstOffer === 'A' ? 'B' : 'A';
    return ((idx % 2) + 2) % 2 === 0 ? cfg.firstOffer : other;
  }
  /** Tier total (cents) for q standard pairs: the table at 1-3, from 4 the 3-pair total per pair, cent exact. */
  function tierTotalFor(cfg, q) {
    if (!(q > 0)) return 0;
    if (q <= 3) return cfg.tierTotal[q];
    return Math.round((q * cfg.tierTotal[3]) / 3);
  }
  /** Even split of `amount` cents into n parts; the first parts carry the odd cents. */
  function split(amount, n) {
    if (!(n > 0)) return [];
    const base = Math.floor(amount / n);
    let rem = amount - base * n;
    return Array.from({ length: n }, () => { const v = base + (rem > 0 ? 1 : 0); if (rem > 0) rem -= 1; return v; });
  }
  /**
   * pairs: array of booleans (true = a 10,000mAh pair); offer: 'off' | 'A' | 'B'.
   * Returns cents: base (the tier total for Q standard pairs), upgradeCharge
   * (per upgraded pair today), extraOff (day B), total, regular (Q x the
   * single-pair price + upgrade per upgraded pair, the strike anchor), save,
   * targets (per pair, before extraOff; they sum to base + upgrades).
   */
  function quote(cfg, pairs, offer) {
    const q = Array.isArray(pairs) ? pairs.length : 0;
    if (!q) return { pairs: 0, base: 0, upgradeCharge: 0, extraOff: 0, total: 0, regular: 0, save: 0, targets: [] };
    const base = tierTotalFor(cfg, q);
    const upgradeCharge = offer === 'A' ? 0 : cfg.upgrade;
    const parts = split(base, q);
    const targets = pairs.map((u, i) => parts[i] + (u ? upgradeCharge : 0));
    const subtotal = targets.reduce((a, b) => a + b, 0);
    const extraOff = offer === 'B' && q >= 2 ? Math.min(cfg.dayBOff, subtotal) : 0;
    const total = subtotal - extraOff;
    const regular = pairs.reduce((s, u) => s + cfg.tierTotal[1] + (u ? cfg.upgrade : 0), 0);
    return { pairs: q, base, upgradeCharge, extraOff, total, regular, save: Math.max(0, regular - total), targets };
  }
  return { toCents, normalizeConfig, jdn, parseDate, offerFor, tierTotalFor, split, quote };
})();
/* ef-pricing-core:end */

const EF_TZ = 'America/New_York';
const IS_BROWSER = typeof window !== 'undefined' && typeof document !== 'undefined';
const REDUCED_MOTION = IS_BROWSER && window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

/* ------------------------------------------------------------------ money */

function formatMoney(cents, format) {
  const fmt = format || (IS_BROWSER && window.themeSettings?.moneyFormat) || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  const [whole, dec] = value.split('.');
  const withThousands = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return fmt
    .replace(/\{\{\s*amount\s*\}\}/g, `${withThousands(',')}.${dec}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withThousands(','))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${withThousands('.')},${dec}`)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withThousands('.'));
}

/* ---------------------------------------------------------- New York clock */

const NY_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: EF_TZ, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
});
const pad2 = (n) => String(n).padStart(2, '0');

/** Wall-clock parts in New York for an instant (ms since epoch). */
export function nyParts(ms) {
  const out = {};
  for (const p of NY_FORMAT.formatToParts(new Date(ms))) if (p.type !== 'literal') out[p.type] = parseInt(p.value, 10);
  if (out.hour === 24) out.hour = 0;
  return { y: out.year, m: out.month, d: out.day, h: out.hour, mi: out.minute, s: out.second };
}
/** "YYYY-MM-DD" in New York for an instant. */
export function nyDate(ms) {
  const p = nyParts(ms);
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`;
}
/** UTC minus New York wall time at an instant, in ms (+4h in EDT, +5h in EST). */
function offsetAt(ms) {
  const p = nyParts(ms);
  return ms - Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
}
/** The instant of the next 00:00:00 in New York after `ms` (settles across a DST change). */
export function nextNyMidnight(ms) {
  const p = nyParts(ms);
  const wallNext = Date.UTC(p.y, p.m - 1, p.d + 1, 0, 0, 0);
  let t = wallNext + offsetAt(ms);
  for (let i = 0; i < 4; i += 1) {
    const q = nyParts(t);
    const diff = Date.UTC(q.y, q.m - 1, q.d, q.h, q.mi, q.s) - wallNext;
    if (diff === 0) break;
    t -= diff;
  }
  return t;
}
/** `days` business days after today (New York), weekends skipped, as a UTC-midnight Date. */
export function shipsByDate(ms, days) {
  const p = nyParts(ms);
  let y = p.y; let m = p.m; let d = p.d;
  let added = 0;
  while (added < days) {
    const n = new Date(Date.UTC(y, m - 1, d + 1));
    y = n.getUTCFullYear(); m = n.getUTCMonth() + 1; d = n.getUTCDate();
    const wd = n.getUTCDay();
    if (wd !== 0 && wd !== 6) added += 1;
  }
  return new Date(Date.UTC(y, m - 1, d));
}

/* ----------------------------------------------------------------- state */

const ef = { cfg: EF_PRICING.normalizeConfig({}, 'cents'), today: { offer: 'off', date: '' }, clockOffset: 0, preview: null, fakeBase: null, fakeStart: 0 };

/** The day's record from the shop metafield evenfall.today (written by Shopify Flow): { offer: 'off' | 'A' | 'B', date: 'YYYY-MM-DD' }. */
export function normalizeToday(raw) {
  const t = raw && typeof raw === 'object' ? raw : {};
  return { offer: t.offer === 'A' || t.offer === 'B' ? t.offer : 'off', date: String(t.date || '').slice(0, 10) };
}
/**
 * Round 9: today's offer is what Flow opened at the checkout, never a date
 * calculation: the record must name today's New York date, and the master
 * switch (evenfall.offer rotation_on) must be on. Anything else is 'off',
 * so the page never promises a perk the checkout would not give (and at
 * midnight the perk simply disappears until the next record arrives).
 */
export function todayOffer(cfg, today, localDate) {
  if (!cfg || !cfg.rotationOn || !today) return 'off';
  if (today.offer !== 'A' && today.offer !== 'B') return 'off';
  return today.date === localDate ? today.offer : 'off';
}

/** Server-synced "now" (ms). The local preview harness may pin a fake clock that keeps running. */
export function now() {
  if (ef.fakeBase !== null) return ef.fakeBase + (Date.now() - ef.fakeStart);
  return Date.now() + ef.clockOffset;
}

function loadConfig() {
  const node = document.querySelector('[data-ef-offer]');
  let raw = {};
  try { raw = JSON.parse(node?.textContent || '{}'); } catch { raw = {}; }
  ef.cfg = EF_PRICING.normalizeConfig(raw, 'cents');
  ef.today = normalizeToday(raw.today);
  const serverNow = Number(raw.server?.now) || 0;
  if (serverNow) ef.clockOffset = serverNow * 1000 - Date.now();
  ef.preview = null;
  const harness = window.EF_PREVIEW;
  // Theme settings > Evenfall > "Flash sale preview" (printed by ef-offer as preview.mode):
  // UNPUBLISHED themes only - the live theme (role main) never previews a flash its
  // checkout does not honour. The local QA page may pin the state, the mode and the role.
  let mode = raw.preview && typeof raw.preview.mode === 'string' ? raw.preview.mode : 'off';
  let role = window.Shopify?.theme?.role || '';
  if (harness && typeof harness === 'object') {
    // the local QA page (_preview): ?state=off|A|B pins the offer, ?clock=ISO pins the clock
    if (harness.state === 'A' || harness.state === 'B' || harness.state === 'off') ef.preview = harness.state;
    if (typeof harness.mode === 'string') mode = harness.mode;
    if (typeof harness.role === 'string') role = harness.role;
    if (harness.clock) {
      const t = Date.parse(harness.clock);
      if (Number.isFinite(t)) { ef.fakeBase = t; ef.fakeStart = Date.now(); }
    }
  }
  if (!ef.preview && role && role !== 'main' && (mode === 'A' || mode === 'B' || mode === 'rotation')) ef.preview = mode;
}

/** Today's offer: 'off' | 'A' | 'B' (a pinned preview wins; "rotation" previews an A / B alternation from start_date; then the day Flow opened, if it is today in New York). */
export function currentOffer() {
  if (ef.preview === 'A' || ef.preview === 'B' || ef.preview === 'off') return ef.preview;
  if (ef.preview === 'rotation') return EF_PRICING.offerFor({ ...ef.cfg, rotationOn: true, startDate: ef.cfg.startDate || '2026-10-26' }, nyDate(now()));
  return todayOffer(ef.cfg, ef.today, nyDate(now()));
}

/* -------------------------------------------------------------- flash bar */

function initFlashBar(bar) {
  if (bar.dataset.efInit === '1') return;
  bar.dataset.efInit = '1';
  const KEY = 'tranquira:ef-flash-dismissed';
  const line = bar.querySelector('[data-ef-flash-line]');
  const h = bar.querySelector('[data-ef-hours]');
  const m = bar.querySelector('[data-ef-mins]');
  const s = bar.querySelector('[data-ef-secs]');
  const ends = bar.querySelector('[data-ef-flash-ends]');
  let dismissed = window.__efFlashDismissed === true;
  if (!dismissed) { try { dismissed = sessionStorage.getItem(KEY) === '1'; } catch { /* storage refused: memory only */ } }
  let offer = null;
  let target = 0;

  const setTarget = () => {
    target = nextNyMidnight(now());
    if (ends) {
      try {
        const when = new Date(target).toLocaleString('en-US', { timeZone: EF_TZ, dateStyle: 'short', timeStyle: 'short' });
        ends.textContent = (ends.dataset.endsTemplate || '[date]').replace('[date]', when);
      } catch { /* leave the template */ }
    }
  };
  const render = (announce) => {
    const o = currentOffer();
    const changed = o !== offer;
    offer = o;
    bar.dataset.offer = o;
    if (line) line.textContent = o === 'B' ? (bar.dataset.lineB || '') : (bar.dataset.lineA || '');
    bar.hidden = dismissed || o === 'off';
    if (changed && announce) document.dispatchEvent(new CustomEvent('ef:offer', { detail: { offer: o } }));
  };
  const tick = () => {
    const t = now();
    if (t >= target) { setTarget(); render(true); }
    let left = Math.max(0, Math.floor((target - t) / 1000));
    const hours = Math.floor(left / 3600); left -= hours * 3600;
    const mins = Math.floor(left / 60); left -= mins * 60;
    if (h) h.textContent = pad2(hours);
    if (m) m.textContent = pad2(mins);
    if (s) s.textContent = pad2(left);
    document.dispatchEvent(new CustomEvent('ef:tick', { detail: { offer, text: `${pad2(hours)}:${pad2(mins)}:${pad2(left)}`, target } }));
  };
  setTarget();
  render(false);
  tick();
  // the sticky header sits under the bar (top slot, sections/flash-countdown-bar's
  // Evenfall branch): publish the bar height, 0 while hidden, like the Italy bar does
  const setH = () => document.documentElement.style.setProperty('--flash-bar-h', bar.hidden ? '0px' : `${bar.getBoundingClientRect().height}px`);
  if ('ResizeObserver' in window) new ResizeObserver(setH).observe(bar);
  new MutationObserver(setH).observe(bar, { attributes: true, attributeFilter: ['hidden'] });
  setH();
  let timer = setInterval(tick, 1000);
  document.addEventListener('visibilitychange', () => {
    clearInterval(timer);
    if (!document.hidden) { tick(); timer = setInterval(tick, 1000); }
  });
  document.addEventListener('ef:config', () => { setTarget(); render(true); tick(); });
  bar.querySelector('[data-ef-flash-close]')?.addEventListener('click', () => {
    dismissed = true;
    window.__efFlashDismissed = true;
    try { sessionStorage.setItem(KEY, '1'); } catch { /* memory only */ }
    bar.hidden = true;
  });
}

/* ---------------------------------------------------------------- buy box */

function initBuyBox(root) {
  if (root.dataset.efInit === '1') return;
  root.dataset.efInit = '1';
  initAssurance(root);
  if (root.dataset.usd !== 'true') return;

  let variants = [];
  try { variants = JSON.parse(root.querySelector('[data-ef-variants]')?.textContent || '[]'); } catch { return; }
  const strings = window.themeStrings || {};
  // the page only ever adds the standard pair; the 10,000mAh upgrade is offered in the cart drawer
  const std = variants.find((v) => !v.upgraded) || variants[0] || null;

  const priceEl = root.querySelector('[data-ef-price]');
  const compareEl = root.querySelector('[data-ef-compare]');
  const savePill = root.querySelector('[data-ef-save-pill]');
  const saveText = root.querySelector('[data-ef-save-text]');
  const perk = root.querySelector('[data-ef-perk]');
  const perkText = root.querySelector('[data-ef-perk-text]');
  const banksText = root.querySelector('[data-ef-banks-text]');
  const banksTpl = root.querySelector('[data-ef-banks-row]')?.dataset.template || '[n] power banks included';
  const tiersEl = root.querySelector('[data-ef-tiers]');
  const cards = [...root.querySelectorAll('[data-ef-tier]')];
  const form = root.querySelector('form[action*="/cart/add"]');
  const idInput = root.querySelector('[data-ef-variant-id]');
  const qtyInput = root.querySelector('[data-ef-qty]');
  const atc = root.querySelector('[data-ef-atc]');
  const atcLabel = root.querySelector('[data-ef-atc-label]');
  const errorEl = root.querySelector('[data-ef-error]');
  const errorDefault = errorEl?.textContent || '';
  const amountTpl = savePill?.dataset.amountTemplate || '%s';
  const pctTpl = savePill?.dataset.pctTemplate || '%d%';
  const atcTpl = atc?.dataset.labelTemplate || 'Add to cart · [total]';
  // "per pair" label as Liquid printed it (the text after the amount)
  const perLabel = (cards.map((c) => c.querySelector('[data-ef-tier-per]')?.textContent || '').find((t) => t.trim()) || '').replace(/^\S+\s*/, '').trim();

  let tier = Math.min(3, Math.max(1, parseInt(root.dataset.tier, 10) || 1));
  let submitting = false;
  const flags = (n) => Array.from({ length: n }, () => false);
  const cardFor = (t) => cards.find((c) => (parseInt(c.dataset.efTier, 10) || 1) === t) || null;
  const tierTitle = (t) => {
    const sub = cardFor(t)?.querySelector('.ef-pack__sub')?.textContent || '';
    const first = sub.split('·')[0].trim();
    return first || `${t}`;
  };

  function render() {
    const offer = currentOffer();
    const q = EF_PRICING.quote(ef.cfg, flags(tier), offer);

    // price row: the selection's total, the regular price struck, "Save $X";
    // one pair with nothing to save falls back to the variant's compare-at (percent)
    let was = q.regular > q.total ? q.regular : 0;
    let label = '';
    if (was) label = amountTpl.replace('%s', formatMoney(q.save));
    else if (tier === 1 && std && std.compare_at_price > std.price) {
      was = std.compare_at_price;
      label = pctTpl.replace('%d', String(Math.round(((was - q.total) / was) * 100)));
    }
    if (priceEl) priceEl.textContent = formatMoney(q.total);
    if (compareEl) { compareEl.textContent = was ? formatMoney(was) : ''; compareEl.classList.toggle('hidden', !was); }
    if (savePill) savePill.classList.toggle('hidden', !label);
    if (saveText) saveText.textContent = label;

    // the day's perk line (A: free upgrade in the cart, B: extra off)
    if (perk) {
      perk.hidden = offer === 'off';
      if (perkText) perkText.textContent = offer === 'B' ? (perk.dataset.lineB || '') : (perk.dataset.lineA || '');
    }
    // power banks included: 2 per pair of the selected pack
    if (banksText) banksText.textContent = banksTpl.replace('[n]', String(tier * 2));

    // pack cards: each prices its own number of standard pairs for today
    cards.forEach((card) => {
      const t = parseInt(card.dataset.efTier, 10) || 1;
      const qt = EF_PRICING.quote(ef.cfg, flags(t), offer);
      const total = card.querySelector('[data-ef-tier-total]');
      const full = card.querySelector('[data-ef-tier-full]');
      const save = card.querySelector('[data-ef-tier-save]');
      const per = card.querySelector('[data-ef-tier-per]');
      let anchor = qt.regular > qt.total ? qt.regular : 0;
      if (t === 1 && !anchor && std && std.compare_at_price > std.price) anchor = std.compare_at_price;
      if (total) total.textContent = formatMoney(qt.total);
      if (full) { full.hidden = !anchor; full.textContent = anchor ? formatMoney(anchor) : ''; }
      if (save) { save.hidden = !(qt.save > 0); save.textContent = qt.save > 0 ? amountTpl.replace('%s', formatMoney(qt.save)) : ''; }
      if (per) { per.hidden = t === 1; per.textContent = t === 1 ? '' : `${formatMoney(Math.round(qt.total / t))} ${perLabel}`.trim(); }
      const on = t === tier;
      card.setAttribute('aria-checked', String(on));
      card.tabIndex = on ? 0 : -1;
    });

    // form + button: the standard variant x pairs
    const available = !!(std && std.available);
    if (idInput) idInput.value = std ? String(std.id) : '';
    if (qtyInput) qtyInput.value = String(tier);
    root.dataset.tier = String(tier);
    if (atc) {
      atc.disabled = !available;
      if (atcLabel) atcLabel.textContent = available ? atcTpl.replace('[total]', formatMoney(q.total)) : (strings.soldOut || 'Sold out');
    }

    // sticky bar mirror (sections/ef-sticky-bar)
    document.dispatchEvent(new CustomEvent('ef:render', { detail: { total: q.total, was, label, tier, tierTitle: tierTitle(tier), offer, available } }));
  }

  function applyTier(next, focus) {
    const t = Math.min(3, Math.max(1, next));
    if (t !== tier) { tier = t; render(); }
    if (focus) cardFor(tier)?.focus();
  }

  tiersEl?.addEventListener('click', (e) => {
    const card = e.target.closest('[data-ef-tier]');
    if (!card || !tiersEl.contains(card)) return;
    applyTier(parseInt(card.dataset.efTier, 10) || 1, false);
  });
  tiersEl?.addEventListener('keydown', (e) => {
    if (!e.target.closest('[data-ef-tier]')) return;
    const keys = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1, Home: 'first', End: 'last' };
    if (!(e.key in keys)) return;
    e.preventDefault();
    const dir = keys[e.key];
    if (dir === 'first') { applyTier(1, true); return; }
    if (dir === 'last') { applyTier(cards.length, true); return; }
    const i = cards.findIndex((c) => c.getAttribute('aria-checked') === 'true');
    const next = cards[((i === -1 ? 0 : i) + dir + cards.length) % cards.length];
    applyTier(parseInt(next.dataset.efTier, 10) || 1, true);
  });
  // midnight rollover / editor preview change
  document.addEventListener('ef:offer', render);

  /* ---- add to cart: one item (standard variant x pairs) through the drawer
     controller; the drawer renders from that response and opens itself. The
     native form is the no-JS path (one pair). ---- */
  const clearLoading = () => {
    submitting = false;
    atc?.classList.remove('is-loading');
    atc?.querySelector('.pdp-buy__atc-spinner')?.setAttribute('hidden', '');
  };
  const setLoading = () => {
    if (!atc) return;
    atc.classList.add('is-loading');
    atc.querySelector('.pdp-buy__atc-spinner')?.removeAttribute('hidden');
    const drawer = document.getElementById('CartDrawer');
    if (drawer) {
      const mo = new MutationObserver(() => {
        if (drawer.classList.contains('is-open')) { clearLoading(); mo.disconnect(); }
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      setTimeout(() => { clearLoading(); mo.disconnect(); }, 8000);
    } else {
      setTimeout(clearLoading, 8000);
    }
  };
  if (form && atc) {
    form.addEventListener('submit', (e) => {
      if (submitting) { e.preventDefault(); e.stopPropagation(); return; }
      e.preventDefault();
      e.stopPropagation();
      submitting = true;
      if (errorEl) { errorEl.textContent = errorDefault; errorEl.hidden = true; }
      setLoading();
      atc.disabled = true;
      (async () => {
        try {
          if (!std || !std.available) throw new Error('variant unavailable');
          const items = [{ id: std.id, quantity: tier }];
          const opener = document.querySelector('[data-open-cart]');
          const drawer = document.getElementById('CartDrawer');
          if (window.TranquiraCart?.add && drawer) {
            await window.TranquiraCart.add(items, { handle: form.dataset.productHandle });
            if (!drawer.classList.contains('is-open')) clearLoading();
          } else {
            const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items })
            });
            if (!res.ok) throw new Error(await res.text());
            if (opener && drawer) {
              opener.click();
              setTimeout(() => { if (!drawer.classList.contains('is-open')) clearLoading(); }, 2500);
            } else {
              window.location.href = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
            }
          }
        } catch (err) {
          console.error('[ef-buybox]', err);
          if (errorEl) { errorEl.textContent = errorDefault; errorEl.hidden = false; }
          clearLoading();
        } finally {
          atc.disabled = false;
        }
      })();
    });
  }

  render();
}

/* --------------------------------------------------------- assurance card */

/** US holidays the delivery window skips: Thanksgiving (4th Thursday of November), Christmas Day, New Year's Day. */
export function isUsHoliday(y, m, d, weekday) {
  if (m === 12 && d === 25) return true;
  if (m === 1 && d === 1) return true;
  return m === 11 && weekday === 4 && d >= 22 && d <= 28;
}
/** The dates `minDays` and `maxDays` business days after today (New York), weekends and the holidays skipped. */
export function deliveryWindow(ms, minDays, maxDays) {
  const p = nyParts(ms);
  let y = p.y; let m = p.m; let d = p.d;
  let count = 0; let from = null; let to = null;
  for (let i = 0; i < 60 && !to; i += 1) {
    const n = new Date(Date.UTC(y, m - 1, d + 1));
    y = n.getUTCFullYear(); m = n.getUTCMonth() + 1; d = n.getUTCDate();
    const wd = n.getUTCDay();
    if (wd === 0 || wd === 6 || isUsHoliday(y, m, d, wd)) continue;
    count += 1;
    if (count === minDays) from = new Date(Date.UTC(y, m - 1, d));
    if (count === maxDays) to = new Date(Date.UTC(y, m - 1, d));
  }
  return { from, to };
}
function initAssurance(root) {
  const el = root.querySelector('[data-ef-assure]');
  if (!el) return;
  const range = el.querySelector('[data-ef-delivery-range]');
  const minDays = parseInt(el.dataset.minDays, 10) || 0;
  const maxDays = parseInt(el.dataset.maxDays, 10) || 0;
  if (range && maxDays) {
    const fmt = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    const paint = () => {
      const w = deliveryWindow(now(), Math.min(minDays, maxDays), maxDays);
      if (w.from && w.to) range.textContent = `${fmt(w.from)} - ${fmt(w.to)}`;
    };
    paint();
    document.addEventListener('ef:offer', paint); // a new day in New York
  }
  // the mocha segment between Ordered and On its way draws in once, when the card scrolls into view
  const timeline = el.querySelector('[data-ef-timeline]');
  if (!timeline) return;
  const draw = () => timeline.classList.add('is-drawn');
  if (REDUCED_MOTION.matches || !('IntersectionObserver' in window)) { draw(); return; }
  const io = new IntersectionObserver((entries) => {
    if (!entries.some((en) => en.isIntersecting)) return;
    draw();
    io.disconnect();
  }, { threshold: 0.6 });
  io.observe(timeline);
}

/* ------------------------------------------------------------ back to top */

/* shows once the buy box has left the viewport (scrolled past), hides near the
   footer; the CSS lifts it above the sticky Add to cart bar while that is visible */
function initBackToTop(btn) {
  if (btn.dataset.efInit === '1') return;
  btn.dataset.efInit = '1';
  const buyBox = () => document.querySelector('[data-ef-buybox]');
  let ticking = false;
  const update = () => {
    ticking = false;
    const box = buyBox();
    const past = box ? box.getBoundingClientRect().bottom < 0 : window.scrollY > 600;
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    const nearFooter = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
    btn.hidden = false;
    btn.classList.toggle('is-visible', past && !nearFooter);
  };
  const schedule = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  update();
  btn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  });
}

/* ------------------------------------------------------------ social proof */

/* Judge.me source: the app's widget HTML for THIS product sits in an inert
   <template>; cards are built from its text (textContent only, never
   innerHTML). Old widget (.jdgm-rev) and the revamp (.jm-review-item) are both
   read. Editor block cards are server-rendered and need nothing here. When
   neither source yields a card the section hides itself. */
function initReviews(root) {
  if (root.dataset.efInit === '1') return;
  root.dataset.efInit = '1';
  const rail = root.querySelector('[data-ef-reviews-rail]');
  const tpl = root.querySelector('template[data-ef-jm-source]');
  const max = parseInt(root.dataset.max, 10) || 6;
  const verifiedLabel = root.dataset.verifiedLabel || '';
  if (rail && tpl) {
    const doc = tpl.content;
    const items = [...doc.querySelectorAll('.jdgm-rev, .jm-review-item, [data-review-id]')];
    const text = (el, sel) => (el.querySelector(sel)?.textContent || '').replace(/\s+/g, ' ').trim();
    const star = (on) => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '14'); svg.setAttribute('height', '14');
      svg.setAttribute('fill', on ? 'currentColor' : 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1'); svg.setAttribute('aria-hidden', 'true');
      const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      poly.setAttribute('points', '12,3.8 14.53,9.12 20.37,9.88 16.09,13.93 17.17,19.72 12,16.9 6.83,19.72 7.91,13.93 3.63,9.88 9.47,9.12');
      svg.appendChild(poly);
      return svg;
    };
    let made = 0;
    for (const it of items) {
      if (made >= max) break;
      const body = text(it, '.jdgm-rev__body, .jm-review-content__body, [class*="review-body"], [class*="__body"]');
      const title = text(it, '.jdgm-rev__title, .jm-review-content__title, [class*="__title"]');
      const author = text(it, '.jdgm-rev__author, .jm-review-author__name, [class*="author"]');
      const pic = it.querySelector('.jdgm-rev__pic-img, img[class*="pic"], img[class*="media"]')?.getAttribute('data-src') || it.querySelector('.jdgm-rev__pic-img, img[class*="pic"], img[class*="media"]')?.getAttribute('src') || '';
      const scoreEl = it.querySelector('[data-score]');
      const rating = Math.max(0, Math.min(5, Math.round(parseFloat(scoreEl?.getAttribute('data-score') || '0') || it.querySelectorAll('.jdgm-star.jdgm--on').length)));
      const verified = !!it.querySelector('.jdgm-rev__buyer-badge, [class*="verified"]');
      if (!body && !pic) continue;
      const card = document.createElement('article');
      card.className = 'ef-review';
      if (pic && /^https?:\/\//.test(pic)) {
        const img = document.createElement('img');
        img.className = 'ef-review__photo'; img.src = pic; img.alt = ''; img.loading = 'lazy'; img.decoding = 'async'; img.width = 320; img.height = 320;
        card.appendChild(img);
      }
      if (rating) {
        const stars = document.createElement('span');
        stars.className = 'ef-review__stars'; stars.setAttribute('role', 'img'); stars.setAttribute('aria-label', `${rating} out of 5 stars`);
        for (let i = 1; i <= 5; i += 1) stars.appendChild(star(i <= rating));
        card.appendChild(stars);
      }
      if (title) { const h = document.createElement('h3'); h.className = 'ef-review__title'; h.textContent = title; card.appendChild(h); }
      if (body) { const p = document.createElement('p'); p.className = 'ef-review__quote'; p.textContent = body; card.appendChild(p); }
      const foot = document.createElement('footer');
      foot.className = 'ef-review__foot';
      if (author) { const n = document.createElement('span'); n.className = 'ef-review__name'; n.textContent = author; foot.appendChild(n); }
      if (verified && verifiedLabel) { const v = document.createElement('span'); v.className = 'ef-review__verified'; v.textContent = verifiedLabel; foot.appendChild(v); }
      card.appendChild(foot);
      rail.appendChild(card);
      made += 1;
    }
  }
  if (!root.querySelector('.ef-review')) {
    root.hidden = true;
    root.closest('.shopify-section')?.classList.add('is-empty');
  }
}

/* ------------------------------------------------------------ sticky bar */

/* ---------------------------------------------------------- looping video
   sections/pdp-lifestyle-collage's "Looping video" (round 8): the UGC rail's
   ambient contract (assets/pdp-below.js) on one frame. The <source> holds
   data-src only until the frame is within ~200px of the viewport, so an
   off-screen loop never downloads or decodes; leaving the viewport pauses,
   returning resumes. Nothing is drawn over the video: a click on it toggles
   pause / play, and the keyboard-only 44x44 button (visible on focus) is the
   WCAG 2.2.2 pause mechanism. Reduced motion: poster only, no autoplay (the
   toggle still lets someone start it on purpose); switching reduce on while
   it plays pauses it. A rejected play() (data saver, battery saver) leaves
   the poster. */
function initMotion(fig) {
  const video = fig.querySelector('video');
  if (!video || video.dataset.efMotionReady) return;
  video.dataset.efMotionReady = '1';
  const btn = fig.querySelector('[data-ef-motion-pause]');
  const labels = { play: fig.dataset.playLabel || 'Play video', pause: fig.dataset.pauseLabel || 'Pause video' };
  let activated = false;
  let userPaused = false;
  const activate = () => {
    if (activated) return;
    activated = true;
    video.querySelectorAll('source[data-src]').forEach((source) => { source.src = source.dataset.src; });
    video.load();
  };
  const tryPlay = () => {
    activate();
    video.muted = true;
    const p = video.play();
    if (p) p.catch(() => {});
  };
  const syncBtn = () => { if (btn) btn.textContent = video.paused ? labels.play : labels.pause; };
  const toggle = () => {
    if (video.paused) { userPaused = false; tryPlay(); } else { userPaused = true; video.pause(); }
  };
  video.addEventListener('click', toggle);
  btn?.addEventListener('click', (e) => { e.stopPropagation(); toggle(); });
  ['play', 'pause'].forEach((ev) => video.addEventListener(ev, syncBtn));
  syncBtn();
  if (REDUCED_MOTION.addEventListener) REDUCED_MOTION.addEventListener('change', (e) => { if (e.matches && !video.paused) { userPaused = true; video.pause(); } });
  if (REDUCED_MOTION.matches || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) { if (!userPaused && video.paused) tryPlay(); } else if (!video.paused) video.pause();
    });
  }, { rootMargin: '200px 0px' });
  io.observe(fig);
}

function initStickyBar(bar) {
  if (bar.dataset.efInit === '1') return;
  bar.dataset.efInit = '1';
  const atc = document.querySelector('[data-ef-atc]');
  const price = bar.querySelector('[data-ef-sticky-price]');
  const compare = bar.querySelector('[data-ef-sticky-compare]');
  const save = bar.querySelector('[data-ef-sticky-save]');
  const title = bar.querySelector('[data-ef-sticky-title]');
  const ends = bar.querySelector('[data-ef-sticky-ends]');
  const endsText = bar.querySelector('[data-ef-sticky-ends-text]');
  const base = bar.querySelector('[data-ef-sticky-jump]')?.dataset.titleBase || '';

  document.addEventListener('ef:render', (e) => {
    const d = e.detail || {};
    if (price) price.textContent = formatMoney(d.total || 0);
    if (compare) { compare.textContent = d.was ? formatMoney(d.was) : ''; compare.hidden = !d.was; }
    if (save) { save.textContent = d.label || ''; save.hidden = !d.label; }
    if (title) title.textContent = d.tierTitle ? `${base} · ${d.tierTitle}` : base;
    const btn = bar.querySelector('[data-ef-sticky-atc]');
    if (btn) btn.disabled = d.available === false;
  });
  document.addEventListener('ef:tick', (e) => {
    if (!ends) return;
    const on = e.detail?.offer === 'A' || e.detail?.offer === 'B';
    ends.hidden = !on;
    if (on && endsText) endsText.textContent = e.detail.text;
  });

  if (!atc) return;
  // shows only once the main Add to cart button has scrolled UP out of view; never while that
  // button or the pack picker is on screen (the bar would cover the picker), nor with the drawer open
  const picker = document.querySelector('[data-ef-tiers]');
  let drawerOpen = false;
  let ticking = false;
  let shown = null;
  const onScreen = (r) => r.bottom > 0 && r.top < window.innerHeight;
  const compute = () => {
    ticking = false;
    const a = atc.getBoundingClientRect();
    const atcAbove = a.bottom <= 0;
    const pickerVisible = !!picker && onScreen(picker.getBoundingClientRect());
    const show = atcAbove && !onScreen(a) && !pickerVisible && !drawerOpen;
    if (show === shown) return;
    shown = show;
    if (show) bar.hidden = false;
    requestAnimationFrame(() => bar.classList.toggle('is-visible', show));
  };
  const update = () => { if (!ticking) { ticking = true; requestAnimationFrame(compute); } };
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
  const drawer = document.getElementById('CartDrawer');
  if (drawer) {
    new MutationObserver(() => { drawerOpen = drawer.classList.contains('is-open'); update(); }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }
  // a buy box initialised before this bar (editor section reload) has already rendered: ask for a fresh render
  if (document.querySelector('[data-ef-buybox][data-ef-init="1"]')) document.dispatchEvent(new CustomEvent('ef:offer', { detail: { offer: currentOffer() } }));
  bar.querySelector('[data-ef-sticky-atc]')?.addEventListener('click', () => {
    if (atc.disabled || atc.classList.contains('is-loading')) return;
    const form = document.getElementById('EfProductForm');
    if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
  });
  document.addEventListener('click', (e) => {
    const jump = e.target.closest('[data-ef-sticky-jump]');
    if (!jump) return;
    document.querySelector('.pdp-tiers')?.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
  });
}

/* ------------------------------------------------------------------- boot */

/* section wrappers that render nothing (an empty UGC rail, a disabled section's
   empty wrapper, hidden social proof) and fixed sections carry no vertical
   margin on this template */
/* ------------------------------------------------------- reviews carousel
   sections/ef-reviews-rail: the bra rail behaviour (pdp.js initArrows +
   initDotCarousel) on the review cards - arrows and ArrowLeft / ArrowRight
   move one card, Home / End jump to the ends, dots mark the card nearest
   the snap line (on desktop the first of the three in view), the arrows
   disable at the ends and hide while nothing overflows, every scroll is
   instant under reduced motion. Swipe and trackpad are the native
   overflow scroll with snap. In the editor, selecting a Review block
   scrolls its card into view. */

function initReviewsRail(root) {
  if (root.dataset.efInit === '1') return;
  root.dataset.efInit = '1';
  const track = root.querySelector('[data-ef-rail-track]');
  if (!track) return;
  const frame = root.querySelector('[data-ef-rail-frame]');
  const prev = root.querySelector('[data-ef-rail-prev]');
  const next = root.querySelector('[data-ef-rail-next]');
  const dotsWrap = root.querySelector('[data-ef-rail-dots]');
  const cards = () => [...track.querySelectorAll('[data-ef-rail-card]')];
  const behavior = () => (REDUCED_MOTION.matches ? 'auto' : 'smooth');
  // each card's scroll position: where scrollLeft puts it on the snap line (the track's start padding)
  const positions = () => {
    const t = track.getBoundingClientRect();
    const pad = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
    return cards().map((c) => Math.max(0, Math.round(c.getBoundingClientRect().left - t.left + track.scrollLeft - pad)));
  };
  const maxScroll = () => track.scrollWidth - track.clientWidth;
  const current = () => {
    const pos = positions();
    if (!pos.length) return 0;
    let best = 0;
    pos.forEach((p, i) => { if (Math.abs(p - track.scrollLeft) < Math.abs(pos[best] - track.scrollLeft)) best = i; });
    return best;
  };
  const goTo = (i) => {
    const pos = positions();
    if (!pos.length) return;
    const idx = Math.max(0, Math.min(pos.length - 1, i));
    track.scrollTo({ left: Math.min(pos[idx], maxScroll()), behavior: behavior() });
  };
  let dots = [];
  if (dotsWrap) {
    const list = cards();
    if (list.length < 2) dotsWrap.hidden = true;
    else {
      list.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.dataset.index = String(i);
        dot.setAttribute('aria-label', `${i + 1} / ${list.length}`);
        if (i === 0) dot.classList.add('is-active');
        dotsWrap.appendChild(dot);
      });
      dots = [...dotsWrap.children];
      dotsWrap.addEventListener('click', (e) => {
        const dot = e.target.closest('[data-index]');
        if (dot) goTo(parseInt(dot.dataset.index, 10));
      });
    }
  }
  let raf = 0;
  const sync = () => {
    raf = 0;
    const max = maxScroll();
    const i = current();
    dots.forEach((d, j) => d.classList.toggle('is-active', i === j));
    if (frame) frame.classList.toggle('no-overflow', max < 4);
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  };
  const queueSync = () => { if (!raf) raf = requestAnimationFrame(sync); };
  track.addEventListener('scroll', queueSync, { passive: true });
  window.addEventListener('resize', queueSync, { passive: true });
  prev?.addEventListener('click', () => goTo(current() - 1));
  next?.addEventListener('click', () => goTo(current() + 1));
  track.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current() + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current() - 1); }
    else if (e.key === 'Home') { e.preventDefault(); goTo(0); }
    else if (e.key === 'End') { e.preventDefault(); goTo(cards().length - 1); }
  });
  sync();
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:block:select', (e) => {
      const card = e.target && e.target.closest ? e.target.closest('[data-ef-rail-card]') : null;
      if (card && track.contains(card)) goTo(cards().indexOf(card));
    });
  }
}

function markEmptySections() {
  document.querySelectorAll('.ef-template .main > .shopify-section').forEach((w) => {
    const empty = !w.firstElementChild || w.getBoundingClientRect().height === 0;
    w.classList.toggle('is-empty', empty);
  });
}

function initAll(scope) {
  const s = scope || document;
  s.querySelectorAll('[data-ef-reviews]').forEach(initReviews);
  markEmptySections();
  s.querySelectorAll('[data-ef-flash-bar]').forEach(initFlashBar);
  // the sticky bar listens for the buy box's ef:render: register it first so the first render fills it
  s.querySelectorAll('[data-ef-stickybar]').forEach(initStickyBar);
  s.querySelectorAll('[data-ef-buybox]').forEach(initBuyBox);
  s.querySelectorAll('[data-ef-backtotop]').forEach(initBackToTop);
  s.querySelectorAll('[data-ef-rail]').forEach(initReviewsRail);
  s.querySelectorAll('[data-ef-motion]').forEach(initMotion);
}

if (IS_BROWSER) {
  loadConfig();
  initAll(document);
  if (window.Shopify?.designMode) {
    // the theme editor re-renders a section as new DOM without handlers; a changed
    // "Flash preview" setting must also reach the buy box (ef:config -> ef:offer)
    ['shopify:section:load', 'shopify:section:reorder'].forEach((evt) => document.addEventListener(evt, (e) => {
      loadConfig();
      initAll(e.target && e.target.querySelectorAll ? e.target : document);
      document.dispatchEvent(new CustomEvent('ef:config'));
      document.dispatchEvent(new CustomEvent('ef:offer', { detail: { offer: currentOffer() } }));
    }));
  }
}
